import {
	AfterViewInit,
	Component,
	Input,
	OnDestroy,
	QueryList,
	ViewChild,
	ViewChildren,
} from '@angular/core';
import { DeploymentContext } from '@Common';
import { ReviewEditsService } from '../../services/review-edits.service.js';
import { UpdateComponentBaseWithEditItems } from '../../UpdateComponentBaseWithEditItems.js';
import { NotificationsComponent } from '../notifications/notifications.component.js';
import {
	Deal,
	Funding,
	ICompanyUpdate,
	IDealUpdate,
	IFundingUpdate,
	IModelEntity,
	LocalizedTextIds,
} from 'company-finder-common';
import _ from 'lodash-es';
import { DealModalComponent } from '../deal-modal/deal-modal.component.js';
import { FundingModalComponent } from '../funding-modal/funding-modal.component.js';
import { CompanyService } from '@Common';
import { CompanyUpdateService } from '../../services/company-update.service.js';
import { ApproveDeclineComponent } from '../approve-decline/approve-decline.component.js';
import { EditablePropertyGroup } from '../../company-update.interface.js';
@Component({
	selector: 'jnj-information-update',
	templateUrl: './jnj-information-update.component.html',
	styleUrls: ['./jnj-information-update.component.scss'],
	standalone: false,
})
export class JnJInformationUpdateComponent
	extends UpdateComponentBaseWithEditItems
	implements OnDestroy, AfterViewInit
{
	// I'd really rather have something cleaner than passing in the component,
	// but I also don't want this current refactor to grow bigger than it
	// already needs to be. I'm adding a backlog story to revisit this, and I'll
	// add the link to it here once it exists (not doing it now in case I change my approach)
	@Input()
	public notificationsComponent: NotificationsComponent;

	@ViewChild('fundingModalComponent', { static: true })
	public fundingModalComponent: FundingModalComponent;
	@ViewChild('dealModalComponent', { static: true })
	public dealModalComponent: DealModalComponent;

	@ViewChildren(ApproveDeclineComponent)
	public approveDeclineComponents: QueryList<ApproveDeclineComponent>;

	public showFundingModal = false;
	public showDealModal = false;

	public get dealToModify(): Deal {
		return this.updateService.dealToModify;
	}

	public set dealToModify(value: Deal) {
		this.updateService.dealToModify = value;
	}

	public get fundingToModify(): Funding {
		return this.updateService.fundingToModify;
	}

	public set fundingToModify(value: Funding) {
		this.updateService.fundingToModify = value;
	}

	// Local getters to prevent having lots of code like
	// this._companyUpdateService.getCurrentUpdate().dealUpdate
	// which get a bit unweildy to read
	private get currentUpdate(): ICompanyUpdate {
		return this.updateService.getCurrentUpdate();
	}

	private get dealUpdates(): IDealUpdate[] {
		return this.currentUpdate.dealUpdates;
	}

	private get fundingUpdates(): IFundingUpdate[] {
		return this.currentUpdate.fundingUpdates;
	}

	public get haveFundingsChanged(): boolean {
		return this.updateService.haveFundingsChanged;
	}

	public set haveFundingsChanged(value: boolean) {
		this.updateService.haveFundingsChanged = value;
	}

	public get enableDiversityQuestions(): boolean {
		return this._deploymentContext.featureSwitches.companyUpdateSwitches
			.enableDiversityQuestions;
	}

	public get leadershipDiversity(): string {
		const formControl = this.parentForm.get('leadershipDiversity');
		return formControl?.value;
	}

	public set leadershipDiversity(value: string) {
		const formControl = this.parentForm.get('leadershipDiversity');
		formControl?.setValue(value);
	}

	public get boardAdvisorDiversity(): string {
		const formControl = this.parentForm.get('boardAdvisorDiversity');
		return formControl?.value;
	}

	public set boardAdvisorDiversity(value: string) {
		const formControl = this.parentForm.get('boardAdvisorDiversity');
		formControl?.setValue(value);
	}

	public isAdded(obj: Deal | Funding, typeId: string): boolean {
		return (
			this.updateService.isNewItem(obj) &&
			(!this.isCurrentUpdateApproved() ||
				!this.isDealOrFundingFromPriorUpdate(obj, typeId))
		);
	}

	public isApproved(obj: Deal | Funding, typeId: string): boolean {
		return (
			this.updateService.isNewItem(obj) &&
			this.isCurrentUpdateApproved() &&
			this.isDealOrFundingFromPriorUpdate(obj, typeId)
		);
	}

	public constructor(
		dc: DeploymentContext,
		private _reviewEditsService: ReviewEditsService,
		_companyService: CompanyService,
		_companyUpdateService: CompanyUpdateService
	) {
		super(dc, _companyUpdateService, _companyService);
	}

	public async ngAfterViewInit(): Promise<void> {
		this.setAllSubscriptions();
	}

	public ngOnDestroy(): void {
		this._subscriptions?.unsubscribe();
	}

	public setAllSubscriptions(): void {
		this.setReviewEditsSubscriptions();
		this.setFundingSubscriptions();
		this.setDealSubscriptions();
		super.setAllSubscriptions();
	}

	public setReviewEditsSubscriptions(): void {
		this.addSubscription(
			this._reviewEditsService.showReviewModalSubject.subscribe(
				(item: unknown) => {
					this.showDealOrFundingFromReview(item);
				}
			)
		);
	}

	public setFundingSubscriptions(): void {
		this.addSubscription(
			this.fundingModalComponent.closingSubject.subscribe(
				async (result: { funding: Funding; isSave: boolean }) => {
					await this.closeFunding(result);
				}
			)
		);

		this.addSubscription(
			this.fundingModalComponent.approveDeclineSubject.subscribe(
				(result: { funding: Funding; isApproved: boolean }) => {
					this.showFundingModal = false;

					const approveDeclineComponent =
						this.approveDeclineComponents.find(
							(comp) =>
								comp.funding &&
								comp.funding.fundingId ===
									result.funding.fundingId
						);
					if (approveDeclineComponent) {
						if (result.isApproved) {
							approveDeclineComponent.approveFunding(
								result.funding
							);
						} else {
							approveDeclineComponent.declineFunding(
								result.funding
							);
						}
					}
				}
			)
		);
	}

	public setDealSubscriptions(): void {
		this.addSubscription(
			this.dealModalComponent.closingSubject.subscribe(
				async (result: { deal: Deal; isSave: boolean }) => {
					await this.closeDeal(result);
				}
			)
		);
		this.addSubscription(
			this.dealModalComponent.approveDeclineSubject.subscribe(
				(result: { deal: Deal; isApproved: boolean }) => {
					this.showDealModal = false;

					const approveDeclineComponent =
						this.approveDeclineComponents.find(
							(comp) =>
								comp.deal &&
								comp.deal.dealId === result.deal.dealId
						);
					if (approveDeclineComponent) {
						if (result.isApproved) {
							approveDeclineComponent.approveDeal(result.deal);
						} else {
							approveDeclineComponent.declineDeal(result.deal);
						}
					}
				}
			)
		);
	}

	private showDealOrFundingFromReview(item: {
		dealId?: string;
		fundingId?: string;
	}): void {
		if (item.dealId) {
			this.openDeal(item as Deal);
		}
		if (item.fundingId) {
			this.openFunding(item as Funding);
		}
	}

	private async closeDeal(result: {
		deal: Deal;
		isSave: boolean;
	}): Promise<void> {
		this.showDealModal = false;
		// We're not saving, so undo any changes to the deals
		if (!result.isSave && result.deal.dealId) {
			const originalDeal = this.companyWithMostRecentEdit.deals.find(
				(d) => d.dealId === result.deal.dealId
			);
			// Existing deals would be in the companyClone, new ones in the user's current session would not.
			const dealToRestore = originalDeal
				? _.cloneDeep(originalDeal)
				: result.deal;
			const index = this.company.deals.findIndex(
				(d) => d.dealId === result.deal.dealId
			);
			this.company.deals.splice(index, 1, dealToRestore);
		}

		if (!result.isSave) {
			return;
		}

		if (this.updateService.isNewItem(result.deal)) {
			const index = this.company.deals.findIndex(
				(d) => d.dealId === result.deal.dealId
			);
			if (!result.deal.isDeleted) {
				// Don't double-save
				if (index < 0) {
					this.company.deals.push(result.deal);
				}
			} else {
				// They deleted a deal they just tried to add, so remove it from the company.
				this.company.deals.splice(index, 1);
				if (this.pendingUpdate) {
					// Clean up the DB
					const updateType = 'deal';
					const deleteCompanyUpdate =
						this.updateService.wouldBeEmptyCompanyUpdate(
							this.pendingUpdate
						);
					await this.companyService.deletePendingAddedComplexUpdateByModelId(
						this.company.modelId,
						updateType,
						result.deal.dealId,
						deleteCompanyUpdate
					);
					// Apply the pending update, assuming there is one.
					// If there isn't, i.e., deleteCompanyUpdate is true, applySavedOrPendingUpdateData()
					// does little more than retrieve a null company update, thus setting pendingUpdate to to null.
					// NOTE: Don't call initializeUpdateData() here because:
					// * We already have any approved but unprocessed update data, and nothing we've done in the modal impacts that stuff.
					// * We want the pending update applied to the company, but don't want to clone the company into the
					//   Pending cache or we'll lose track of what's different.
					await this.updateService.applySavedOrPendingUpdateData();
					// Make sure we remove the deleted deal from the caches
					_.remove(
						this.companyClone.deals,
						(d) => d.dealId === result.deal.dealId
					);
					_.remove(
						this.companyWithPending.deals,
						(d) => d.dealId === result.deal.dealId
					);
				}
				// The DB is clean, so now make sure the companyWithMostRecentEdit reflects the current state of the company.
				this.companyWithMostRecentEdit =
					this.updateService.cloneCompany(this.company);
				await this.notificationsComponent.getNotification();
			}
		}

		_.merge(this.companyWithMostRecentEdit.deals, this.company.deals);
		this.updateService.checkUpdatesForUI();

		this.haveDealsChanged = this.dealUpdates?.length > 0;
	}

	private async closeFunding(result: {
		funding: Funding;
		isSave: boolean;
	}): Promise<void> {
		this.showFundingModal = false;

		// We're not saving, so undo any changes to the funding
		if (!result.isSave && result.funding.fundingId) {
			const originalFunding = this.companyWithMostRecentEdit.funding.find(
				(f) => f.fundingId === result.funding.fundingId
			);
			// Existing fundings would be in the companyClone, new ones in the user's current session would not.
			const fundingToRestore = originalFunding
				? _.cloneDeep(originalFunding)
				: result.funding;
			const index = this.company.funding.findIndex(
				(f) => f.fundingId === result.funding.fundingId
			);
			this.company.funding.splice(index, 1, fundingToRestore);
		}

		if (!result.isSave) {
			return;
		}

		if (this.updateService.isNewItem(result.funding)) {
			const index = this.company.funding.findIndex(
				(f) => f.fundingId === result.funding.fundingId
			);
			if (!result.funding.isDeleted) {
				// Don't double-save
				if (index < 0) {
					this.company.funding.push(result.funding);
				}
			} else {
				// They deleted a funding they just tried to add, so remove it from the company.
				this.company.funding.splice(index, 1);
				if (this.pendingUpdate) {
					// Clean up the DB
					const updateType = 'funding';
					const deleteCompanyUpdate =
						this.updateService.wouldBeEmptyCompanyUpdate(
							this.pendingUpdate
						);
					await this.companyService.deletePendingAddedComplexUpdateByModelId(
						this.company.modelId,
						updateType,
						result.funding.fundingId,
						deleteCompanyUpdate
					);
					// Apply the pending update, assuming there is one.
					// If there isn't, i.e., deleteCompanyUpdate is true, applySavedOrPendingUpdateData()
					// does little more than retrieve a null company update, thus setting pendingUpdate to to null.
					// NOTE: Don't call initializeUpdateData() here because:
					// * We already have any approved but unprocessed update data, and nothing we've done in the modal impacts that stuff.
					// * We want the pending update applied to the company, but don't want to clone the company into the
					//   Pending cache or we'll lose track of what's different.
					await this.updateService.applySavedOrPendingUpdateData();
					// Make sure we remove the deleted deal from the caches
					_.remove(
						this.companyClone.funding,
						(f) => f.fundingId === result.funding.fundingId
					);
					_.remove(
						this.companyWithPending.funding,
						(f) => f.fundingId === result.funding.fundingId
					);
				}
				// The DB is clean, so now make sure the companyWithMostRecentEdit reflects the current state of the company.
				this.companyWithMostRecentEdit =
					this.updateService.cloneCompany(this.company);
				await this.notificationsComponent.getNotification();
			}
		}

		_.merge(this.companyWithMostRecentEdit.funding, this.company.funding);
		this.updateService.checkUpdatesForUI();

		this.haveFundingsChanged = this.fundingUpdates?.length > 0;
	}

	public IsNumberOrNumberString(value: unknown): boolean {
		return !isNaN(parseFloat(value as string)) && isFinite(value as number);
	}

	public isCurrentUpdateApproved(): boolean {
		return this.updateService.isCurrentUpdateApproved(
			this.notificationsComponent
		);
	}

	public isDealOrFundingFromPriorUpdate(
		obj: Deal | Funding,
		typeId: string
	): boolean {
		const updates: IModelEntity[] =
			typeId === 'dealId' ? this.dealUpdates : this.fundingUpdates;

		return !!_.find(updates, (update) => update.id === obj[typeId]);
	}

	public openFunding(funding?: Funding): void {
		this.fundingToModify = funding ?? new Funding();
		this.showFundingModal = true;
	}

	public openDeal(deal?: Deal): void {
		this.dealToModify = deal ?? new Deal();
		this.showDealModal = true;
	}

	public get miscProperties(): EditablePropertyGroup {
		const group: EditablePropertyGroup = {
			properties: [],
		};
		if (this.featureSwitches.companyUpdateSwitches.enableKeyManagement) {
			group.properties.push('keyMgmtAndAdvBm');
		}
		if (
			this.featureSwitches.companyUpdateSwitches
				.enableFirstTimeEntrepreneur
		) {
			group.properties.push('firstTimeEntrepreneur');
		}
		return group;
	}

	public get deiProperties(): EditablePropertyGroup {
		const deiGroup: EditablePropertyGroup = {
			header: LocalizedTextIds.JnjInformationDiversityEquity,
			properties: ['countryForDeiReporting'],
		};

		if (this.enableDiversityQuestions) {
			deiGroup.properties.push(
				'leadershipDiversity',
				'boardAdvisorDiversity'
			);
		}

		deiGroup.properties.push(
			'womenLedOrgLeadership',
			'womenLedBoardOfDirectorsOrEquiv'
		);

		return deiGroup;
	}
}
